import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Menu, ShoppingBag, User, Settings, Palette, Store, X } from 'lucide-react';
import BriefcaseBusinessIcon from './icons/BriefcaseBusinessIcon';
import { useTranslation } from 'react-i18next';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useKiosk } from '../contexts/KioskContext';
import { trackClick } from '../services/analyticsService';
import { getUnreadNotificationCount } from '../services/adminService';
import LanguageSwitcher from './LanguageSwitcher';
import { ADD_TO_CART_FEEDBACK_MS, CART_BADGE_BLINK_MS } from '../constants';

const Header = () => {
  const { totalItems, cartOpenSignal } = useCart();
  const { isAdmin, session } = useAuth();
  const { isKiosk } = useKiosk();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [unreadCount, setUnreadCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // True when the sidebar was opened automatically after add-to-cart rather
  // than via the hamburger button — see the cartOpenSignal effect below.
  const [sidebarAutoOpened, setSidebarAutoOpened] = useState(false);
  const [badgeBlink, setBadgeBlink] = useState(false);
  const closeBtnRef = useRef(null);
  const autoCloseTimerRef = useRef(null);
  const openDelayTimerRef = useRef(null);
  const badgeBlinkTimerRef = useRef(null);

  const clearAutoCloseTimer = () => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
  };

  const clearAllCartFeedbackTimers = useCallback(() => {
    clearAutoCloseTimer();
    clearTimeout(openDelayTimerRef.current);
    clearTimeout(badgeBlinkTimerRef.current);
    openDelayTimerRef.current = null;
    badgeBlinkTimerRef.current = null;
  }, []);

  // Admin-only header items (Settings, notification Bell) are also hidden on a
  // public-facing kiosk, even if the machine happens to be signed in as admin.
  // The language toggle, by contrast, is visible on kiosk regardless of who's
  // signed in — store visitors need it.
  const showAdminItems = isAdmin && !isKiosk;

  // Poll unread notifications every 60s while the user is admin.
  useEffect(() => {
    if (!showAdminItems || !session?.access_token) {
      setUnreadCount(0);
      return undefined;
    }
    let cancelled = false;

    const tick = () => {
      getUnreadNotificationCount(session.access_token)
        .then((data) => { if (!cancelled) setUnreadCount(data?.count || 0); })
        .catch(() => { /* silent — badge is best-effort */ });
    };

    tick();
    const interval = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [showAdminItems, session?.access_token]);

  const closeSidebar = useCallback(() => {
    clearAutoCloseTimer();
    setSidebarOpen(false);
    setSidebarAutoOpened(false);
  }, []);

  // Al agregar un producto al carrito, se espera a que termine la animación
  // "¡Agregado!" del botón (ProductCard) antes de reaccionar aquí: hace
  // parpadear el ícono de la bolsa y, en móvil, muestra brevemente el sidebar
  // (menú lateral derecho) para confirmar que el artículo entró a la bolsa.
  // A diferencia de abrirlo con la hamburguesa, esto NO bloquea el resto de
  // la página (sin overlay ni scroll-lock) — es solo una confirmación pasiva
  // que se cierra sola tras un momento.
  useEffect(() => {
    if (!cartOpenSignal) return;
    clearTimeout(openDelayTimerRef.current);
    openDelayTimerRef.current = setTimeout(() => {
      setBadgeBlink(true);
      clearTimeout(badgeBlinkTimerRef.current);
      badgeBlinkTimerRef.current = setTimeout(() => setBadgeBlink(false), CART_BADGE_BLINK_MS);

      if (window.innerWidth <= 768) {
        setSidebarAutoOpened(true);
        setSidebarOpen(true);
        clearAutoCloseTimer();
        autoCloseTimerRef.current = setTimeout(() => {
          setSidebarOpen(false);
          setSidebarAutoOpened(false);
          autoCloseTimerRef.current = null;
        }, 2500);
      }
    }, ADD_TO_CART_FEEDBACK_MS);
  }, [cartOpenSignal]);

  useEffect(() => clearAllCartFeedbackTimers, [clearAllCartFeedbackTimers]);

  // Bloquea scroll del body y habilita cierre con Escape mientras el sidebar está
  // abierto manualmente. Cuando se auto-abrió por un add-to-cart, el resto de la
  // página se mantiene interactiva (ver comentario arriba).
  useEffect(() => {
    if (!sidebarOpen || sidebarAutoOpened) return undefined;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') closeSidebar(); };
    document.addEventListener('keydown', onKey);
    closeBtnRef.current?.focus();
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [sidebarOpen, sidebarAutoOpened, closeSidebar]);

  const handleLogoClick = () => {
    trackClick('logo', 'link', 'Bluppimart Logo', { action: 'navigate_home' });
  };

  const handleAccountClick = () => {
    trackClick('account-button', 'button', 'Cuenta', { action: 'navigate_account' });
    navigate('/account');
  };

  const handleCartClick = () => {
    trackClick('cart-button', 'button', 'Carrito de Compras', {
      action: 'navigate_cart',
      cartItems: totalItems
    });
    navigate('/cart');
  };

  return (
    <>
      <header className="header">
        <div className="header-content">
          <Link
            to="/"
            className="logo"
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '0.5rem',
              fontFamily: "'Gambado Sans', sans-serif"
            }}
            onClick={handleLogoClick}
          >
            <img
              src={isKiosk ? '/kiosk-logo.png' : '/lightIcon.png'}
              alt="Bluppimart Logo"
              className="header-logo-img"
              style={{
                width: '54px',
                height: '48px',
                objectFit: 'contain',
                display: 'block'
              }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            Bluppimart
          </Link>

          <nav className="nav">
            {/* Ítems de desktop — ocultos en móvil, reemplazados por el sidebar */}
            <div className="nav-desktop">
              {showAdminItems && (
                <Link
                  to="/admin"
                  className="admin-nav-link"
                  aria-label={t('header.admin')}
                >
                  <Settings size={24} />
                </Link>
              )}
              <Link
                to="/"
                className="store-nav-link"
                aria-label="Tienda"
                onClick={() => trackClick('store-link', 'link', 'Tienda', { action: 'navigate_home' })}
              >
                <Store size={24} />
              </Link>
              <Link
                to="/artists"
                className="artists-nav-link"
                aria-label={t('header.artists')}
                onClick={() => trackClick('artists-link', 'link', 'Artistas', { action: 'navigate_artists' })}
              >
                <Palette size={24} color="#000" />
              </Link>
              <Link
                to="/allies"
                className="allies-nav-link"
                aria-label="Aliados"
                onClick={() => trackClick('allies-link', 'link', 'Aliados', { action: 'navigate_allies' })}
              >
                <BriefcaseBusinessIcon size={24} />
              </Link>
              <button
                className="account-button"
                onClick={handleAccountClick}
                aria-label={t('header.account')}
              >
                <User size={24} />
              </button>
              <button
                className="cart-button"
                onClick={handleCartClick}
                aria-label={t('header.cart')}
              >
                <ShoppingBag size={24} />
                {totalItems > 0 && (
                  <span className={`cart-badge${badgeBlink ? ' cart-badge-blink' : ''}`}>
                    {totalItems > 99 ? '99+' : totalItems}
                  </span>
                )}
              </button>
              {showAdminItems && (
                <Link
                  to="/admin/notifications"
                  className="notification-bell"
                  aria-label={`Notificaciones${unreadCount > 0 ? ` (${unreadCount} sin leer)` : ''}`}
                  onClick={() => trackClick('notifications-bell', 'link', 'Notificaciones', { unreadCount })}
                >
                  <Bell size={22} />
                  {unreadCount > 0 && (
                    <span className="notification-badge">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
              )}
              {/* Language toggle hidden for the admin UI (Spanish-only) but always
                  visible on the public kiosk so visitors can pick a locale. */}
              {!showAdminItems && <LanguageSwitcher />}
            </div>

            {/* Hamburguesa — solo visible en móvil */}
            <button
              className="hamburger-btn"
              onClick={() => { clearAllCartFeedbackTimers(); setSidebarAutoOpened(false); setSidebarOpen(true); }}
              aria-label="Abrir menú de navegación"
              aria-expanded={sidebarOpen}
              aria-controls="mobile-sidebar"
            >
              <Menu size={24} />
            </button>
          </nav>
        </div>
      </header>

      {/* Overlay — cierra el sidebar al tocar fuera. Oculto cuando el sidebar se
          auto-abrió por un add-to-cart, para no bloquear el resto de la página. */}
      <div
        className={`sidebar-overlay${sidebarOpen && !sidebarAutoOpened ? ' sidebar-overlay--open' : ''}`}
        onClick={closeSidebar}
        aria-hidden="true"
      />

      {/* Sidebar móvil */}
      <aside
        id="mobile-sidebar"
        className={`sidebar${sidebarOpen ? ' sidebar--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menú de navegación"
      >
        <div className="sidebar-header">
          <span className="sidebar-brand">Bluppimart</span>
          <button
            ref={closeBtnRef}
            className="sidebar-close-btn"
            onClick={closeSidebar}
            aria-label="Cerrar menú"
          >
            <X size={22} />
          </button>
        </div>

        <nav className="sidebar-nav">
          <Link
            to="/"
            className="sidebar-item sidebar-item--store"
            onClick={() => { trackClick('store-link', 'link', 'Tienda', { action: 'navigate_home' }); closeSidebar(); }}
          >
            <Store size={22} />
            <span>Tienda</span>
          </Link>
          <Link
            to="/artists"
            className="sidebar-item sidebar-item--artists"
            onClick={() => { trackClick('artists-link', 'link', 'Artistas', { action: 'navigate_artists' }); closeSidebar(); }}
          >
            <Palette size={22} color="#000" />
            <span>{t('header.artists')}</span>
          </Link>

          <Link
            to="/allies"
            className="sidebar-item sidebar-item--allies"
            onClick={() => { trackClick('allies-link', 'link', 'Aliados', { action: 'navigate_allies' }); closeSidebar(); }}
          >
            <BriefcaseBusinessIcon size={22} />
            <span>Aliados</span>
          </Link>

          <button
            className="sidebar-item sidebar-item--account"
            onClick={() => { handleAccountClick(); closeSidebar(); }}
          >
            <User size={22} />
            <span>{t('header.account')}</span>
          </button>

          <button
            className="sidebar-item sidebar-item--cart"
            onClick={() => { handleCartClick(); closeSidebar(); }}
          >
            <ShoppingBag size={22} />
            <span>{t('header.cart')}</span>
            {totalItems > 0 && (
              <span className={`sidebar-cart-badge${badgeBlink ? ' cart-badge-blink' : ''}`}>{totalItems > 99 ? '99+' : totalItems}</span>
            )}
          </button>

          <div className="sidebar-lang">
            <LanguageSwitcher menuPlacement="up" />
          </div>
        </nav>
      </aside>
    </>
  );
};

export default Header;
